import React, { useState, useEffect } from 'react';
import { Box, Typography, LinearProgress, Alert, Chip, Collapse, FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import { Download, FileDownload, ExpandMore, ExpandLess, Settings } from '@mui/icons-material';
import { ExtractField, EXTRACT_FIELDS_CONFIG, DEFAULT_EXTRACT_FIELDS, ESSENTIAL_EXTRACT_FIELDS } from '../../../types/spendingDemand.types';
import { useSpendingDemandManager } from '../../../hooks';
import { useMutation } from '@tanstack/react-query';
import SpendingDemandAPI from '../../../services/SpendingDemandAPI';
import useQueryURL from '../../../../../utils/useQueryURL';

export default function ExtractButtonV2() {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showFieldSelector, setShowFieldSelector] = useState(false);
    
    // Utiliser le hook manager pour obtenir le filtre au format useGenericQuery
    const { allSpendingDemandList } = useSpendingDemandManager({
        disableQuery: false
    });

    // Récupérer orderBy depuis l'URL
    const { getQueryValue } = useQueryURL();
    const orderBy = getQueryValue('spendingdemandmanager-orderBy') || undefined;

    // États locaux pour l'extraction V2
    const [extractProcessId, setExtractProcessId] = useState<string | null>(null);
    const [extractStatus, setExtractStatus] = useState<{
        status: 'PENDING' | 'COMPLETED' | 'ERROR' | 'NOT_FOUND' | null;
        total: number;
        actual: number;
    }>({ status: null, total: 0, actual: 0 });
    const [isExtracting, setIsExtracting] = useState(false);
    const [selectedExtractFieldsLocal, setSelectedExtractFieldsLocal] = useState<ExtractField[]>(DEFAULT_EXTRACT_FIELDS);

    // Récupérer le whereQuery depuis useGenericAPI (currentFilter)
    const whereQuery = allSpendingDemandList.currentFilter || undefined;
    // Recherche plein-texte Description (param descSearch)
    const descSearch = allSpendingDemandList.descSearchValue || undefined;

    const handleStartExtract = () => {
        startExtract.mutate();
    };

    const handleDownloadExtract = () => {
        if (extractProcessId) {
            downloadExtract.mutate(extractProcessId);
        }
    };

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    const toggleFieldSelector = () => {
        setShowFieldSelector(!showFieldSelector);
    };

    const handleFieldChange = (field: ExtractField, checked: boolean) => {
        if (!checked && ESSENTIAL_EXTRACT_FIELDS.includes(field)) {
            return;
        }

        setSelectedExtractFieldsLocal((prevFields) => {
            if (checked) {
                if (prevFields.includes(field)) {
                    return prevFields;
                }
                return [...prevFields, field];
            }
            return prevFields.filter(f => f !== field);
        });
    };

    const resetToDefaultFields = () => {
        setSelectedExtractFieldsLocal(DEFAULT_EXTRACT_FIELDS);
    };

    const selectAllFields = () => {
        setSelectedExtractFieldsLocal(Object.keys(EXTRACT_FIELDS_CONFIG) as ExtractField[]);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'PENDING':
                return 'warning';
            case 'COMPLETED':
                return 'success';
            case 'ERROR':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'PENDING':
                return 'En cours...';
            case 'COMPLETED':
                return 'Terminé';
            case 'ERROR':
                return 'Erreur';
            case 'NOT_FOUND':
                return 'Introuvable';
            default:
                return 'Prêt';
        }
    };

    const getProgressPercentage = () => {
        if (extractStatus.total === 0) return 0;
        return Math.round((extractStatus.actual / extractStatus.total) * 100);
    };

    // Mutation pour démarrer l'extraction V2
    const startExtract = useMutation({
        mutationFn: async () => {
            setIsExtracting(true);
            setExtractStatus({ status: null, total: 0, actual: 0 });
            
            try {
                const result = await SpendingDemandAPI.startExtractSpendingDemandsManagerAPI(
                    whereQuery || undefined,
                    orderBy || undefined,
                    selectedExtractFieldsLocal,
                    undefined,
                    descSearch || undefined
                );
                
                setExtractProcessId(result.processID);
                return result;
            } catch (error) {
                setIsExtracting(false);
                throw error;
            }
        },
        onError: (error) => {
            setIsExtracting(false);
            setExtractStatus({ status: 'ERROR', total: 0, actual: 0 });
            console.error('Erreur lors de l\'extraction:', error);
        }
    });

    // Mutation pour vérifier le statut
    const checkExtractStatus = useMutation({
        mutationFn: async (processID: string) => {
            return await SpendingDemandAPI.getExtractStatusManagerAPI(processID);
        },
        onSuccess: (data) => {
            setExtractStatus({
                status: data.status as 'PENDING' | 'COMPLETED' | 'ERROR' | 'NOT_FOUND',
                total: data.total,
                actual: data.actual
            });
            if (data.status === 'COMPLETED' || data.status === 'ERROR') {
                setIsExtracting(false);
            }
        },
        onError: (error) => {
            setIsExtracting(false);
            setExtractStatus({ status: 'ERROR', total: 0, actual: 0 });
            console.error('Erreur lors de la vérification du statut:', error);
        }
    });

    // Mutation pour télécharger l'extrait
    const downloadExtract = useMutation({
        mutationFn: async (processID: string) => {
            const response = await SpendingDemandAPI.downloadExtractManagerAPI(processID);
            
            // Créer un blob et télécharger le fichier
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `spending_demands_extract_v2_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            return response;
        },
        onSuccess: () => {
            // Réinitialiser l'état après téléchargement
            setExtractProcessId(null);
            setExtractStatus({ status: null, total: 0, actual: 0 });
        },
        onError: (error) => {
            console.error('Erreur lors du téléchargement:', error);
        }
    });

    // Polling pour vérifier le statut de l'extraction
    useEffect(() => {
        if (!extractProcessId || !isExtracting) return;

        const interval = setInterval(() => {
            checkExtractStatus.mutate(extractProcessId);
        }, 2000); // Vérifier toutes les 2 secondes

        return () => clearInterval(interval);
    }, [extractProcessId, isExtracting, checkExtractStatus]);

    return (
        <Box sx={{ 
            border: '1px solid #e0e0e0', 
            borderRadius: 0.5, 
            backgroundColor: '#fafafa',
            overflow: 'hidden',
            width: '100%',
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Bouton compact */}
            <Box sx={{ 
                p: 0.5, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                cursor: 'pointer',
                minHeight: '32px',
                '&:hover': {
                    backgroundColor: '#f0f0f0'
                }
            }} onClick={toggleExpanded}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FileDownload sx={{ fontSize: 14 }} />
                    <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                        Extraire 
                    </Typography>
                    {/* Indicateur de statut compact */}
                    {(extractStatus.status || isExtracting) && (
                        <Chip 
                            label={isExtracting && !extractStatus.status ? '...' : 
                                   extractStatus.status === 'PENDING' ? '...' :
                                   extractStatus.status === 'COMPLETED' ? '✓' :
                                   extractStatus.status === 'ERROR' ? '!' : '...'} 
                            color={isExtracting && !extractStatus.status ? 'warning' : getStatusColor(extractStatus.status || 'PENDING') as any}
                            size="small"
                            sx={{ height: 16, fontSize: '0.6rem', minWidth: 16 }}
                        />
                    )}
                </Box>
                {isExpanded ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
            </Box>

            {/* Contenu expandable */}
            <Collapse in={isExpanded}>
                <Box sx={{ p: 0.5, borderTop: '1px solid #e0e0e0' }}>
                    {/* Boutons d'action */}
                    {!isExtracting && extractStatus.status !== 'COMPLETED' && (
                        <Box sx={{ mb: 0.5, display: 'flex', gap: 0.5 }}>
                            <button
                                onClick={handleStartExtract}
                                disabled={startExtract.isPending || isExtracting}
                                style={{
                                    width: '120px',
                                    height: '18px',
                                    fontSize: '10px',
                                    backgroundColor: '#ffc107',
                                    color: '#000',
                                    border: 'none',
                                    borderRadius: '3px',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    padding: '0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: (startExtract.isPending || isExtracting) ? 0.7 : 1
                                }}
                            >
                                {(startExtract.isPending || isExtracting) ? 'Démarrage...' : 'Extraire'}
                            </button>
                            <button
                                onClick={toggleFieldSelector}
                                style={{
                                    width: '140px',
                                    height: '18px',
                                    fontSize: '10px',
                                    backgroundColor: '#6c757d',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '3px',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    padding: '0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '4px'
                                }}
                            >
                                <Settings sx={{ fontSize: 8 }} />
                                {showFieldSelector ? 'Masquer champs' : 'Configurer champs'}
                            </button>
                        </Box>
                    )}

                    {/* Sélecteur de champs */}
                    <Collapse in={showFieldSelector}>
                        <Box sx={{ 
                            mb: 0.5, 
                            p: 0.5, 
                            backgroundColor: '#f8f9fa', 
                            borderRadius: '3px', 
                            border: '1px solid #e0e0e0',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            width: '100%'
                        }}>
                            <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 500, mb: 0.5, display: 'block' }}>
                                Champs sélectionnés ({selectedExtractFieldsLocal.length})
                            </Typography>
                            
                            {/* Boutons d'actions rapides */}
                            <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                                <button
                                    onClick={resetToDefaultFields}
                                    style={{
                                        height: '18px',
                                        fontSize: '9px',
                                        backgroundColor: '#17a2b8',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '2px',
                                        cursor: 'pointer',
                                        padding: '0 6px'
                                    }}
                                >
                                    Défaut
                                </button>
                                <button
                                    onClick={selectAllFields}
                                    style={{
                                        height: '18px',
                                        fontSize: '9px',
                                        backgroundColor: '#28a745',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '2px',
                                        cursor: 'pointer',
                                        padding: '0 6px'
                                    }}
                                >
                                    Tout
                                </button>
                            </Box>

                            {/* Champs par catégorie */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
                                {Object.entries(
                                    Object.keys(EXTRACT_FIELDS_CONFIG).reduce((acc, field) => {
                                        const category = EXTRACT_FIELDS_CONFIG[field as ExtractField].category;
                                        if (!acc[category]) acc[category] = [];
                                        acc[category].push(field as ExtractField);
                                        return acc;
                                    }, {} as Record<string, ExtractField[]>)
                                ).map(([category, fields]) => (
                                    <Box key={category} sx={{ flex: '0 1 auto', minWidth: '180px', maxWidth: '250px', alignSelf: 'flex-start' }}>
                                        <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 500, mb: 0.5, display: 'block' }}>
                                            {category} ({fields.filter(f => selectedExtractFieldsLocal.includes(f)).length}/{fields.length})
                                        </Typography>
                                        <FormGroup>
                                            {fields.map(field => (
                                                <FormControlLabel
                                                    key={field}
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={selectedExtractFieldsLocal.includes(field)}
                                                            onChange={(e) => handleFieldChange(field, e.target.checked)}
                                                            disabled={ESSENTIAL_EXTRACT_FIELDS.includes(field)}
                                                            sx={{ 
                                                                '& .MuiSvgIcon-root': { fontSize: 14 },
                                                                py: 0
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                                            {EXTRACT_FIELDS_CONFIG[field].label}
                                                            {ESSENTIAL_EXTRACT_FIELDS.includes(field) && ' *'}
                                                        </Typography>
                                                    }
                                                    sx={{ mb: 0, py: 0 }}
                                                />
                                            ))}
                                        </FormGroup>
                                    </Box>
                                ))}
                            </Box>
                            
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block', mt: 0.5 }}>
                                * Champs obligatoires
                            </Typography>
                        </Box>
                    </Collapse>

                    {/* Affichage détaillé du statut */}
                    {(extractStatus.status || isExtracting) && (
                        <Box sx={{ mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {extractStatus.status === 'PENDING' || (isExtracting && !extractStatus.status) 
                                        ? `${extractStatus.actual || 0} / ${extractStatus.total || 0}` 
                                        : getStatusText(extractStatus.status || 'PENDING')
                                    }
                                </Typography>
                            </Box>

                            {/* Barre de progression */}
                            {(extractStatus.status === 'PENDING' || (isExtracting && !extractStatus.status)) && (
                                <LinearProgress 
                                    variant={extractStatus.total > 0 ? "determinate" : "indeterminate"}
                                    value={extractStatus.total > 0 ? getProgressPercentage() : undefined}
                                    sx={{ mb: 0.5, height: 4 }}
                                />
                            )}

                            {/* Messages */}
                            {extractStatus.status === 'ERROR' && (
                                <Alert severity="error" sx={{ mb: 0.5, py: 0.25, fontSize: '0.7rem' }}>
                                    Erreur d'extraction
                                </Alert>
                            )}

                            {(isExtracting && !extractStatus.status) && (
                                <Alert severity="info" sx={{ mb: 0.5, py: 0.25, fontSize: '0.7rem' }}>
                                    Initialisation...
                                </Alert>
                            )}
                        </Box>
                    )}

                    {/* Bouton de téléchargement */}
                    {extractStatus.status === 'COMPLETED' && extractProcessId && (
                        <button
                            onClick={handleDownloadExtract}
                            disabled={downloadExtract.isPending}
                            style={{
                                width: '100%',
                                height: '18px',
                                fontSize: '10px',
                                backgroundColor: '#4caf50',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                fontWeight: '500',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                                opacity: downloadExtract.isPending ? 0.7 : 1
                            }}
                        >
                            <Download sx={{ fontSize: 8 }} />
                            {downloadExtract.isPending ? 'Téléchargement...' : 'Télécharger'}
                        </button>
                    )}

                    {/* Message d'information */}
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontSize: '0.65rem' }}>
                        Prend en compte les filtres appliqués
                    </Typography>
                </Box>
            </Collapse>
        </Box>
    );
}
